<template>
  <section id="paymentMethodsPage">
    <b-row>
      <b-col md="8" lg="6">
        <b-card>
          <b-card-sub-title class="mb-1">Payment methods</b-card-sub-title>
          <b-card-text class="text-muted mb-2">
            Turn a payment method off to hide it from the deposit page. Use this
            when Revolut / Stripe / Binance temporarily block payments — no code
            change or redeploy needed.
          </b-card-text>

          <div v-if="loading" class="text-center py-3">
            <b-spinner small /> Loading…
          </div>

          <div v-else>
            <div
              v-for="key in orderedKeys"
              :key="key"
              class="d-flex align-items-center justify-content-between py-1 method-row"
            >
              <div>
                <span class="method-label">{{ labels[key] || key }}</span>
                <b-badge
                  :variant="methods[key] ? 'light-success' : 'light-danger'"
                  class="ml-1"
                >
                  {{ methods[key] ? 'Enabled' : 'Disabled' }}
                </b-badge>
              </div>
              <b-form-checkbox
                :checked="methods[key]"
                :disabled="saving === key"
                class="custom-control-primary"
                switch
                inline
                @change="(v) => toggle(key, v)"
              />
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardText,
  BCardSubTitle,
  BFormCheckbox,
  BBadge,
  BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Deposit from '@services/deposit.service'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BCardSubTitle,
    BFormCheckbox,
    BBadge,
    BSpinner,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },
  data: () => ({
    methods: {},
    labels: {},
    loading: true,
    saving: null, // method_key currently being saved
  }),
  computed: {
    // Keep a stable display order based on the label map returned by the API.
    orderedKeys() {
      return Object.keys(this.labels)
    },
  },
  methods: {
    async fetch() {
      this.loading = true
      try {
        const { methods, labels } = await Deposit.adminGetMethods()
        this.methods = methods || {}
        this.labels = labels || {}
      } catch (e) {
        this.notify({ variant: 'danger', text: 'Could not load payment methods.' })
      } finally {
        this.loading = false
      }
    },

    async toggle(key, enabled) {
      this.saving = key
      try {
        const { methods } = await Deposit.adminSetMethod(key, enabled)
        this.methods = methods || { ...this.methods, [key]: enabled }
        this.notify({
          variant: 'success',
          text: `${this.labels[key] || key} is now ${enabled ? 'enabled' : 'disabled'}.`,
        })
      } catch (e) {
        // Revert the visual toggle on failure by re-fetching the source of truth.
        this.notify({ variant: 'danger', text: 'Could not save. Please try again.' })
        this.fetch()
      } finally {
        this.saving = null
      }
    },

    notify({ variant = 'success', text = '' } = {}) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Payment methods',
          icon: 'CreditCardIcon',
          text,
          variant,
        },
      })
    },
  },
  mounted() {
    this.fetch()
  },
}
</script>

<style lang="scss" scoped>
.method-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.method-row:last-child {
  border-bottom: 0;
}
.method-label {
  font-weight: 500;
}
</style>
